import React, { useRef } from 'react';

import * as Sentry from '@sentry/nextjs';
import { Box, Col, Image, Link, PressableBox, Row, Text } from '@opendoor/bricks-next';

import { Awaited, EntryComponent } from '../../cms/entries/entries';
import { IComponentPropertyListings } from '../../declarations/contentful';
import { BUYER_HOST, COSMOS_URL } from '../globals';

type PropertiesReturn = Awaited<ReturnType<typeof propertyListingsLoader>>;

/**
 * Note: this product card attempts to use semantic HTML.
 * The overall structure is:
 * <ul> (entire gallery)
 *   <li> (PropertyCard)
 *     <ul> (attribute list)
 *       <li># Beds
 *       <li># Baths
 *       ...
 *     </ul>
 *     <a><address /></a>
 *   </li>
 *   ...
 * </ul>
 * Each card is a <li> nested under a <ul> when called
 * Attributes are also in a list (with some css to put them inline)
 * The address is in a semantic <address> element
 *
 * The whole card is clickable, but we use an onClick handler for a11y
 * reasons. (only href focuses rather than the whole card focuses)
 *
 */
const PropertyCard: React.FC<PropertyResult> = (props) => {
  const address = `${props.address_line_1}, ${props.city}, ${props.state} ${props.zip}`;

  const ref = useRef<HTMLAnchorElement>(null);
  if (!props.photos[0]?.cdn_paths?.['480']) {
    return null;
  }
  let srcUrl = null;
  try {
    const parsedSrcUrl = new URL(`https://images.opendoor.com${props.photos[0].cdn_paths['480']}`);
    parsedSrcUrl.searchParams.delete('service');
    parsedSrcUrl.searchParams.delete('token');
    parsedSrcUrl.searchParams.delete('w');
    parsedSrcUrl.searchParams.set('service', 'cosmos');
    parsedSrcUrl.searchParams.set('preset', '3:2-sm');
    srcUrl = parsedSrcUrl.toString();
  } catch (e) {
    Sentry.captureException(e);
  }

  if (!srcUrl) {
    return null;
  }

  const details = [`${props.bedrooms} br`, `${props.bathrooms} ba`, `${props.sqft} sqft`];

  return (
    <PressableBox
      onPress={() => {
        ref?.current?.click();
      }}
      analyticsName="cosmos-property-card"
      aria-label={address}
      borderRadius="$8x"
      flexDirection="column"
      alignItems="flex-start"
      width="75%"
      minWidth="75%"
      $largerThanXS={{ width: '50%', minWidth: '50%' }}
      $largerThanSM={{ width: '40%', minWidth: '40%' }}
      $largerThanMD={{ width: '30%', minWidth: '30%' }}
      role="listitem"
      backgroundColor="$backgroundPrimary"
      shadow="$pressed"
      hoverStyle={{
        shadow: '$default',
      }}
      focusVisibleStyle={{
        shadow: '$default',
        outlineColor: '$borderAccentBrand',
        outlineWidth: 2,
      }}
      p="$3"
    >
      <Image
        source={{ uri: srcUrl, width: 480 }}
        alt="Listing exterior"
        resizeMode="cover"
        width="100%"
        height={320}
        borderRadius="$6x"
        overflow="hidden"
      />
      <Col pt="$3" px="$2" width="100%" flex={1} gap="$1" alignItems="flex-start">
        <Text tag="span" typography="$labelSmall" $largerThanMD={{ typography: '$labelLarge' }}>
          ${props.current_list_price.toLocaleString()}
        </Text>
        <Row tag="ul" flexWrap="wrap" $platform-web={{ listStyle: 'none' }}>
          {details.map((detail, i) => (
            <Box tag="li" display="block" key={detail}>
              {i > 0 && (
                <Text
                  tag="span"
                  typography="$bodySmall"
                  $largerThanMD={{ typography: '$bodyMedium' }}
                >
                  {' '}
                  ·{' '}
                </Text>
              )}
              <Text
                tag="span"
                typography="$bodySmall"
                fontWeight="$regular"
                $largerThanMD={{ typography: '$bodyMedium' }}
              >
                {detail}
              </Text>
            </Box>
          ))}
        </Row>
        <Link
          ref={ref}
          aria-label={`Go to ${address} detail page`}
          href={`${COSMOS_URL}${props.public_properties_path}`}
          analyticsName="cosmos-articles-pdp-link"
          onPress={(e) => {
            e.stopPropagation();
          }}
        >
          <Box tag="address" display="block" $platform-web={{ fontStyle: 'normal' }}>
            <Text
              tag="span"
              typography="$bodySmall"
              fontWeight="$regular"
              $largerThanMD={{ typography: '$bodyMedium' }}
            >
              {address}
            </Text>
          </Box>
        </Link>
      </Col>
    </PressableBox>
  );
};

const renderPropertyListings = (
  entry: IComponentPropertyListings,
  resolvedData?: PropertiesReturn,
) => {
  return (
    <Box className="embedded-entry-wrapper" $platform-web={{ gridColumn: '2 / -2' }}>
      <Col gap="$2half" width="100%" $largerThanMD={{ mr: '-10%' }}>
        <Box tag="ul" pb="$6" $platform-web={{ listStyle: 'none' }}>
          <Row
            flexWrap="nowrap"
            alignItems="stretch"
            gap="$10"
            pb="$3"
            pl="$3"
            $largerThanMD={{ flexWrap: 'wrap', pl: '$0' }}
            $platform-web={{ overflowX: 'auto' }}
          >
            {(resolvedData?.properties ?? []).map((x) => {
              return <PropertyCard key={x.id} {...x} />;
            })}
          </Row>
        </Box>
        {resolvedData?.mapsBreadcrumb && (
          <Box display="block" mr="auto" width="100%">
            <Link
              href={`${COSMOS_URL}/homes/${resolvedData.mapsBreadcrumb}`}
              analyticsName={`cosmos-articles-see-more-${entry.fields.title}`}
              aria-label={`Visit extended ${entry.fields.title}`}
            >
              <Text
                tag="span"
                typography="$bodyMedium"
                color="$contentAccentBrand"
                $platform-web={{ textDecoration: 'underline' }}
              >
                See more {entry.fields.title}
              </Text>
            </Link>
          </Box>
        )}
      </Col>
    </Box>
  );
};

type ZoneResult = {
  bounds: Array<[number, number]>;
  center: [number, number];
  breadcrumb_path: string;
};
type PropertyResult = {
  id: string;
  city: string;
  public_properties_path: string;
  state: string;
  zip: string;
  address_line_1: string;
  sqft: number;
  for_sale_at: string;
  published_at: string;
  location: [number, number];
  property_type: string;
  status: string;
  bedrooms: number;
  bathrooms: number;
  current_list_price: number;
  mls_listing_number: string;
  photos: Array<{ cdn_paths: { '480': string } }>;
  neighborhood: string;
  unit_number: string | null;
};

type ListingsReturn = {
  properties: Array<PropertyResult>;
  mapsBreadcrumb: string | null;
};

const propertyListingsLoader = async (
  input: IComponentPropertyListings,
): Promise<ListingsReturn> => {
  if (!input.fields.zoneId) {
    return { properties: [], mapsBreadcrumb: null };
  }
  let res;
  res = await fetch(`${BUYER_HOST}/zones/${input.fields.zoneId}.json`);
  if (res.status !== 200) {
    return { properties: [], mapsBreadcrumb: null };
  }

  const { bounds, breadcrumb_path, center } = (await res.json()) as ZoneResult;

  const searchParams = new URLSearchParams([
    ['page', '1'],
    ['page_size', '9'],
    ['sort', 'newest'],
  ]);
  searchParams.set('bounds[north]', `${bounds[0][0]}`);
  searchParams.set('bounds[south]', `${bounds[1][0]}`);
  searchParams.set('bounds[east]', `${bounds[1][1]}`);
  searchParams.set('bounds[west]', `${bounds[0][1]}`);
  searchParams.set('location[]', `${center[0]}`);
  searchParams.set('location[]', `${center[1]}`);
  res = await fetch(
    `${BUYER_HOST}/zones/${
      input.fields.zoneId
    }/list_properties.json?properties_filter%5Binclude_homebuilder%5D=true&${searchParams.toString()}`,
  );
  if (res.status !== 200) {
    return { properties: [], mapsBreadcrumb: null };
  }

  const propsResult = (await res.json()) as { properties: Array<PropertyResult> };

  return { properties: propsResult.properties, mapsBreadcrumb: breadcrumb_path || null };
};

const PropertyListingsComponent: EntryComponent<IComponentPropertyListings, PropertiesReturn> = {
  render: renderPropertyListings,
  loader: propertyListingsLoader,
};

export default PropertyListingsComponent;
